// src/components/Dashboard/AdminDashboard/VideoReview/VideoReview.jsx
import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../../apiFetch";
import "./VideoReview.css";

/**
 * Format date for day grouping
 */
const formatDateGroup = (dateStr) => {
    const date = new Date(dateStr);

    return date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "short",
        day: "2-digit",
        year: "numeric",
    });
};

/**
 * Group videos by PLATFORM upload date (createdAt)
 */
const groupVideosByDate = (videos) => {
    const grouped = {};

    videos.forEach((video) => {
        const rawDate = video.createdAt;
        if (!rawDate) return;

        const formatted = formatDateGroup(rawDate);

        if (!grouped[formatted]) {
            grouped[formatted] = [];
        }

        grouped[formatted].push(video);
    });

    // Sort videos inside each day (newest first)
    Object.keys(grouped).forEach((key) => {
        grouped[key].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    });

    // Sort days newest first
    return Object.entries(grouped).sort(
        (a, b) => new Date(b[1][0].createdAt) - new Date(a[1][0].createdAt)
    );
};



/**
 * Group REPORTED videos by report date (latestReportAt),
 * falling back to upload date (createdAt) if needed.
 */
const groupReportedVideosByDate = (videos) => {
    const grouped = {};

    videos.forEach((video) => {
        const rawDate = video.latestReportAt || video.createdAt; // ✅ key change
        if (!rawDate) return;

        const formatted = formatDateGroup(rawDate);

        if (!grouped[formatted]) grouped[formatted] = [];
        grouped[formatted].push(video);
    });

    // Sort videos inside each day by report date (newest first)
    Object.keys(grouped).forEach((key) => {
        grouped[key].sort(
            (a, b) =>
                new Date(b.latestReportAt || b.createdAt) - new Date(a.latestReportAt || a.createdAt)
        );
    });

    // Sort days newest first by report date
    return Object.entries(grouped).sort(
        (a, b) =>
            new Date(b[1][0].latestReportAt || b[1][0].createdAt) -
            new Date(a[1][0].latestReportAt || a[1][0].createdAt)
    );
};




const VideoReview = () => {
    // ✅ 3-way toggle now
    const [activeTab, setActiveTab] = useState("videos"); // "videos" | "violations" | "reviews"

    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);

    // NEW: violation tab data
    const [reportedVideos, setReportedVideos] = useState([]);
    const [reportedLoading, setReportedLoading] = useState(false);
    const [reportedError, setReportedError] = useState("");

    const [openDays, setOpenDays] = useState({});
    const [openVideoId, setOpenVideoId] = useState(null); // ✅ one active video (all tabs share)

    // ─────────────────────────────────────────────
    // REVIEWS TAB STATE (player search + suspend/unsuspend)
    // ─────────────────────────────────────────────
    const [playerSearchTerm, setPlayerSearchTerm] = useState("");
    const [playerSearchBy, setPlayerSearchBy] = useState("all"); // all | fullName | username | profileSlug | id | loginEmail
    const [players, setPlayers] = useState([]);
    const [playerLoading, setPlayerLoading] = useState(false);
    const [playerBusyId, setPlayerBusyId] = useState(null);
    const [playerError, setPlayerError] = useState("");

    // Optional: custom duration to suspend (default 7)
    const [suspendDays, setSuspendDays] = useState(7);

    // ─────────────────────────────────────────────
    // Fetch ALL videos (existing behavior)
    // ─────────────────────────────────────────────
    useEffect(() => {
        const fetchVideos = async () => {
            try {
                const res = await apiFetch("/api/admin/all-videos");
                if (!res.ok) throw new Error("Failed to fetch videos");

                const data = await res.json();
                setVideos(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Video fetch failed:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchVideos();
    }, []);

    // ─────────────────────────────────────────────
    // Fetch REPORTED videos (new endpoint)
    // NOTE: you said server.js already prefixes /api/admin
    // so FE calls /api/admin/reported-videos
    // ─────────────────────────────────────────────
    const fetchReportedVideos = async () => {
        try {
            setReportedLoading(true);
            setReportedError("");

            const res = await apiFetch("/api/admin/reported-videos");
            if (!res.ok) throw new Error(await res.text());

            const data = await res.json();
            setReportedVideos(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Reported videos fetch failed:", err);
            setReportedError("Failed to fetch reported videos.");
            setReportedVideos([]);
        } finally {
            setReportedLoading(false);
        }
    };

    // when switching to violations tab, fetch it (lazy load)
    useEffect(() => {
        if (activeTab === "violations") {
            fetchReportedVideos();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const groupedVideos = useMemo(() => {
        return groupVideosByDate(videos);
    }, [videos]);

    const groupedReportedVideos = useMemo(() => {
        return groupReportedVideosByDate(reportedVideos);
    }, [reportedVideos]);

    const toggleDay = (date) => {
        setOpenDays((prev) => ({
            ...prev,
            [date]: !prev[date],
        }));
    };

    const toggleVideo = (videoId) => {
        setOpenVideoId((prev) => (prev === videoId ? null : videoId));
    };

    // ✅ status helpers (reads from backend; falls back to pending if missing)
    const getStatus = (video) => {
        const s = String(video?.moderation_status || video?.moderationStatus || "pending").toLowerCase();
        if (s === "approved" || s === "rejected" || s === "pending") return s;
        return "pending";
    };

    const getStatusLabel = (s) => s.charAt(0).toUpperCase() + s.slice(1);

    // ✅ Approve / Reject handlers (used by Videos tab + Violations tab)
    const handleApprove = async (video, source = "videos") => {
        try {
            const adminId =
                Number(localStorage.getItem("adminId")) || Number(localStorage.getItem("roleId")) || null;

            const res = await apiFetch(`/api/admin/videos/${video.id}/approve`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ adminId }),
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                throw new Error(text || "Approve failed");
            }

            // ✅ update UI without refetch
            setVideos((prev) =>
                prev.map((v) =>
                    v.id === video.id
                        ? {
                            ...v,
                            moderation_status: "approved",
                            moderated_at: new Date().toISOString(),
                            moderated_by: adminId,
                        }
                        : v
                )
            );

            // ✅ update violations list too (and remove it since it's no longer "pending report")
            setReportedVideos((prev) =>
                prev
                    .map((v) =>
                        v.id === video.id
                            ? {
                                ...v,
                                moderation_status: "approved",
                                moderated_at: new Date().toISOString(),
                                moderated_by: adminId,
                            }
                            : v
                    )
                    // typically you only want "pending report" in violations tab
                    .filter((v) => v.id !== video.id)
            );
        } catch (err) {
            console.error("Approve failed:", err);
            alert("Approve failed");
        }
    };

    const handleReject = async (video, source = "videos") => {
        try {
            const adminId =
                Number(localStorage.getItem("adminId")) || Number(localStorage.getItem("roleId")) || null;

            const res = await apiFetch(`/api/admin/videos/${video.id}/reject`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ adminId }),
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                throw new Error(text || "Reject failed");
            }

            // ✅ update UI without refetch
            setVideos((prev) =>
                prev.map((v) =>
                    v.id === video.id
                        ? {
                            ...v,
                            moderation_status: "rejected",
                            moderated_at: new Date().toISOString(),
                            moderated_by: adminId,
                        }
                        : v
                )
            );

            // ✅ update violations list too (and remove it since it's handled)
            setReportedVideos((prev) =>
                prev
                    .map((v) =>
                        v.id === video.id
                            ? {
                                ...v,
                                moderation_status: "rejected",
                                moderated_at: new Date().toISOString(),
                                moderated_by: adminId,
                            }
                            : v
                    )
                    .filter((v) => v.id !== video.id)
            );
        } catch (err) {
            console.error("Reject failed:", err);
            alert("Reject failed");
        }
    };

    // ─────────────────────────────────────────────
    // REVIEWS TAB HELPERS
    // ─────────────────────────────────────────────
    const searchPlayers = async () => {
        const term = playerSearchTerm.trim();
        if (!term) return;

        try {
            setPlayerLoading(true);
            setPlayerError("");

            // Backend endpoint: GET /api/admin/players?search=...&searchBy=...
            const res = await apiFetch(
                `/api/admin/players?search=${encodeURIComponent(term)}&searchBy=${encodeURIComponent(
                    playerSearchBy
                )}`,
                { method: "GET" }
            );

            if (!res.ok) throw new Error(await res.text());

            const data = await res.json();
            setPlayers(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Search failed:", err);
            setPlayerError("Failed to search players.");
            setPlayers([]);
        } finally {
            setPlayerLoading(false);
        }
    };

    const onPlayerSearchKeyDown = (e) => {
        if (e.key === "Enter") searchPlayers();
    };

    // ✅ suspend (admin action)
    // NOTE: PATCH /api/admin/player/:playerId/suspend  body: { days?: number }
    const suspendPlayer = async (playerId) => {
        try {
            setPlayerBusyId(playerId);

            const res = await apiFetch(`/api/admin/player/${playerId}/suspend`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ days: Number(suspendDays) || 7 }),
            });

            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();

            // Try to hydrate updated values from server, else fall back
            const updated = data?.player || data?.updatedPlayer || null;

            setPlayers((prev) =>
                prev.map((p) =>
                    p.id === playerId
                        ? {
                            ...p,
                            isUploadSuspended: updated?.isUploadSuspended ?? true,
                            suspensionEndsAt: updated?.suspensionEndsAt ?? updated?.suspensionEndsAt,
                            isDiscoverable: updated?.isDiscoverable ?? p.isDiscoverable,
                            violationCount: updated?.violationCount ?? p.violationCount,
                        }
                        : p
                )
            );
        } catch (err) {
            console.error("Suspend failed:", err);
            alert("Failed to suspend player.");
        } finally {
            setPlayerBusyId(null);
        }
    };

    // ✅ unsuspend (admin action)
    // NOTE: PATCH /api/admin/player/:playerId/unsuspend
    const unsuspendPlayer = async (playerId) => {
        try {
            setPlayerBusyId(playerId);

            const res = await apiFetch(`/api/admin/player/${playerId}/unsuspend`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
            });

            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();

            const updated = data?.player || data?.updatedPlayer || null;

            setPlayers((prev) =>
                prev.map((p) =>
                    p.id === playerId
                        ? {
                            ...p,
                            isUploadSuspended: updated?.isUploadSuspended ?? false,
                            suspensionEndsAt: updated?.suspensionEndsAt ?? null,
                            isDiscoverable: updated?.isDiscoverable ?? true,
                            violationCount: updated?.violationCount ?? 0,
                        }
                        : p
                )
            );
        } catch (err) {
            console.error("Unsuspend failed:", err);
            alert("Failed to unsuspend player.");
        } finally {
            setPlayerBusyId(null);
        }
    };

    const getPlayerSearchPlaceholder = () => {
        switch (playerSearchBy) {
            case "fullName":
                return "Search by full name (e.g., John Smith)";
            case "username":
                return "Search by username (e.g., busquets)";
            case "profileSlug":
                return "Search by profileSlug (e.g., john-smith-2004)";
            case "loginEmail":
                return "Search by loginEmail (e.g., user@gmail.com)";
            case "id":
                return "Search by Player ID (e.g., 11)";
            default:
                return "Search by name, username, profileSlug, loginEmail, or ID...";
        }
    };

    if (loading) {
        return (
            <div className="admin-video-review">
                <h2>Video Review</h2>
                <p>Loading videos...</p>
            </div>
        );
    }

    // Shared renderer for a grouped video list (Videos tab and Violations tab)
    const renderGroupedVideoList = (groups, emptyText = "No videos found.") => {
        if (!groups || groups.length === 0) return <p>{emptyText}</p>;

        return groups.map(([date, vids]) => (
            <div key={date} className="avr-day-group">
                <div className="avr-day-header" onClick={() => toggleDay(date)}>
                    <h3>
                        {date} ({vids.length})
                    </h3>
                    <span className="avr-arrow">{openDays[date] ? "▲" : "▼"}</span>
                </div>

                {openDays[date] && (
                    <div className="avr-video-grid">
                        {vids.map((video) => {
                            const isOpen = openVideoId === video.youtube_video_id;
                            const status = getStatus(video);

                            return (
                                <div key={video.youtube_video_id} className="avr-video-card">
                                    {/* ✅ Thumbnail OR Player */}
                                    {!isOpen ? (
                                        <div
                                            className="avr-thumbnail"
                                            onClick={() => toggleVideo(video.youtube_video_id)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <img
                                                src={`https://i.ytimg.com/vi/${video.youtube_video_id}/hqdefault.jpg`}
                                                alt={video.title}
                                            />
                                            <div className="avr-play-overlay">▶</div>
                                        </div>
                                    ) : (
                                        <div className="avr-player-wrapper">
                                            <iframe
                                                width="100%"
                                                height="220"
                                                src={`https://www.youtube.com/embed/${video.youtube_video_id}?autoplay=1`}
                                                title={video.title}
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            />
                                            <button className="avr-close-btn" onClick={() => toggleVideo(video.youtube_video_id)}>
                                                Close
                                            </button>
                                        </div>
                                    )}

                                    <div className="avr-video-info">
                                        <h4>{video.title}</h4>

                                        <p className="avr-player">Player: {video.playerName || "Unknown"}</p>

                                        <div className="avr-status">
                                            Status:{" "}
                                            <span className={`avr-status-badge ${status}`}>{getStatusLabel(status)}</span>
                                        </div>

                                        <div className="avr-actions">
                                            <button
                                                className="approve-btn"
                                                onClick={() => handleApprove(video)}
                                                disabled={status === "approved"}
                                                title={status === "approved" ? "Already approved" : "Approve video"}
                                            >
                                                Approve
                                            </button>

                                            <button
                                                className="reject-btn"
                                                onClick={() => handleReject(video)}
                                                disabled={status === "rejected"}
                                                title={status === "rejected" ? "Already rejected" : "Reject video"}
                                            >
                                                Reject
                                            </button>

                                            <button
                                                className="view-btn"
                                                onClick={() =>
                                                    window.open(`https://www.youtube.com/watch?v=${video.youtube_video_id}`, "_blank")
                                                }
                                            >
                                                View on YouTube
                                            </button>
                                        </div>

                                        {video.published_at && (
                                            <p className="avr-meta">
                                                YouTube Published: {new Date(video.published_at).toLocaleDateString()}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        ));
    };

    return (
        <div className="admin-video-review">
            {/* ✅ Centered Title */}
            <h2 className="avr-title">Video Review</h2>

            {/* ✅ Toggle (Videos | Violation Reports | Reviews) */}
            <div className="avr-toggle">
                <button
                    type="button"
                    className={`avr-toggle-btn ${activeTab === "videos" ? "active" : ""}`}
                    onClick={() => setActiveTab("videos")}
                >
                    Videos
                </button>

                <button
                    type="button"
                    className={`avr-toggle-btn ${activeTab === "violations" ? "active" : ""}`}
                    onClick={() => setActiveTab("violations")}
                    title="Videos reported by users"
                >
                    Violation Reports
                </button>

                <button
                    type="button"
                    className={`avr-toggle-btn ${activeTab === "reviews" ? "active" : ""}`}
                    onClick={() => setActiveTab("reviews")}
                >
                    Reviews
                </button>
            </div>

            {/* ─────────────────────────────────────────────
         TAB: VIDEOS (your current UI unchanged)
         ───────────────────────────────────────────── */}
            {activeTab === "videos" && <>{renderGroupedVideoList(groupedVideos, "No videos found.")}</>}

            {/* ─────────────────────────────────────────────
         TAB: VIOLATION REPORTS (NEW)
         Shows videos that were reported by scouts/users
         Admin can Approve or Reject using EXISTING endpoints
         ───────────────────────────────────────────── */}
            {activeTab === "violations" && (
                <>
                    <div className="avr-violations-header">
                        <div className="avr-violations-title-row">
                            <h3 className="avr-violations-title">Violation Reports</h3>
                            <button
                                type="button"
                                className="avr-btn avr-btn--primary"
                                onClick={fetchReportedVideos}
                                disabled={reportedLoading}
                                title="Refresh reported videos"
                            >
                                {reportedLoading ? "Refreshing..." : "Refresh"}
                            </button>
                        </div>

                        <p className="avr-violations-subtitle">
                            These videos were reported by scouts/users. Approve keeps the video. Reject marks it as rejected.
                        </p>

                        {reportedError && <p className="avr-error">{reportedError}</p>}
                    </div>

                    {reportedLoading ? (
                        <p>Loading reported videos...</p>
                    ) : (
                        <>{renderGroupedVideoList(groupedReportedVideos, "No reported videos found.")}</>
                    )}
                </>
            )}

            {/* ─────────────────────────────────────────────
         TAB: REVIEWS (player search + suspend/unsuspend)
         ───────────────────────────────────────────── */}
            {activeTab === "reviews" && (
                <div className="avr-reviews">
                    <div className="avr-reviews-card">
                        <div className="avr-reviews-header">
                            <h3 className="avr-reviews-title">Player Reviews</h3>
                            <p className="avr-reviews-subtitle">
                                Search players to suspend / unsuspend and manage visibility.
                            </p>
                        </div>

                        <div className="avr-reviews-controls">
                            <div className="avr-field">
                                <label className="avr-label">Search By</label>
                                <select
                                    className="avr-select"
                                    value={playerSearchBy}
                                    onChange={(e) => setPlayerSearchBy(e.target.value)}
                                >
                                    <option value="all">All (Recommended)</option>
                                    <option value="fullName">Full Name</option>
                                    <option value="username">Username</option>
                                    <option value="profileSlug">Profile Slug</option>
                                    <option value="loginEmail">Login Email</option>
                                    <option value="id">Player ID</option>
                                </select>
                            </div>

                            <div className="avr-field avr-field--grow">
                                <label className="avr-label">Search</label>
                                <input
                                    className="avr-input"
                                    type="text"
                                    placeholder={getPlayerSearchPlaceholder()}
                                    value={playerSearchTerm}
                                    onChange={(e) => setPlayerSearchTerm(e.target.value)}
                                    onKeyDown={onPlayerSearchKeyDown}
                                />
                            </div>

                            <button
                                className="avr-btn avr-btn--primary"
                                type="button"
                                onClick={searchPlayers}
                                disabled={playerLoading}
                            >
                                {playerLoading ? "Searching..." : "Search"}
                            </button>
                        </div>

                        <div className="avr-reviews-controls avr-reviews-controls--secondary">
                            <div className="avr-field">
                                <label className="avr-label">Suspend Days</label>
                                <input
                                    className="avr-input"
                                    type="number"
                                    min={1}
                                    max={365}
                                    value={suspendDays}
                                    onChange={(e) => setSuspendDays(e.target.value)}
                                />
                            </div>

                            {playerError && <p className="avr-error">{playerError}</p>}
                        </div>

                        <div className="avr-reviews-hint">
                            Tip: press <span className="avr-kbd">Enter</span> to search.
                        </div>
                    </div>

                    <div className="avr-player-results">
                        {players.length === 0 && !playerLoading ? (
                            <div className="avr-empty">
                                <div className="avr-empty-title">No results yet</div>
                                <div className="avr-empty-text">
                                    Search a player by <b>username</b>, <b>full name</b>, <b>profileSlug</b>, <b>loginEmail</b>, or{" "}
                                    <b>ID</b>.
                                </div>
                            </div>
                        ) : (
                            players.map((player) => {
                                const busy = playerBusyId === player.id;
                                const suspended = Boolean(player.isUploadSuspended);
                                const endsAt = player.suspensionEndsAt || player.suspension_ends_at || null;

                                return (
                                    <div key={player.id} className="avr-player-card">
                                        <div className="avr-player-left">
                                            <div className="avr-player-name-row">
                                                <div className="avr-player-name">{player.fullName || "Unnamed Player"}</div>

                                                <span className={`avr-pill ${suspended ? "avr-pill--bad" : "avr-pill--good"}`}>
                                                    {suspended ? "Restricted" : "Active"}
                                                </span>

                                                <span className={`avr-pill ${player.isDiscoverable ? "avr-pill--good" : "avr-pill--bad"}`}>
                                                    {player.isDiscoverable ? "Discoverable" : "Hidden"}
                                                </span>
                                            </div>

                                            <div className="avr-player-meta">
                                                <div className="avr-meta-item">
                                                    <span className="avr-meta-label">Username</span>
                                                    <span className="avr-meta-value">{player.username ? `@${player.username}` : "N/A"}</span>
                                                </div>

                                                <div className="avr-meta-item">
                                                    <span className="avr-meta-label">Player ID</span>
                                                    <span className="avr-meta-value">{player.id}</span>
                                                </div>

                                                <div className="avr-meta-item">
                                                    <span className="avr-meta-label">Strikes</span>
                                                    <span className="avr-meta-value">{player.violationCount ?? 0}</span>
                                                </div>

                                                <div className="avr-meta-item">
                                                    <span className="avr-meta-label">Restriction Ends</span>
                                                    <span className="avr-meta-value">{endsAt ? new Date(endsAt).toLocaleString() : "—"}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="avr-player-actions">
                                            {!suspended ? (
                                                <button
                                                    className="avr-btn avr-btn--danger"
                                                    type="button"
                                                    disabled={busy}
                                                    onClick={() => suspendPlayer(player.id)}
                                                    title="Suspend / restrict this player"
                                                >
                                                    {busy ? "Working..." : "Suspend"}
                                                </button>
                                            ) : (
                                                <button
                                                    className="avr-btn avr-btn--outline"
                                                    type="button"
                                                    disabled={busy}
                                                    onClick={() => unsuspendPlayer(player.id)}
                                                    title="Lift restriction immediately"
                                                >
                                                    {busy ? "Working..." : "Unsuspend"}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default VideoReview;